import {useMemo} from 'react';
import {escapeRegExp} from '@core/helpers';
import {SubCategory} from './useInstantChartsData';
import {includesInLC, startsFromInLC} from "@datassential/platform-ui-lib";

export interface SearchMatches {
    subCategoryMatches: Map<string, number | null>;
    chartMatches: Map<string, Set<string>>;
    subCategoriesNameMatches: string[];
    subCategoriesNameStarts: string[];
    chartsNameMatches: string[];
    chartsNameStarts: string[];
}

export function useSearchMatches(
    search: string,
    subCategories: SubCategory[],
) {
    return useMemo(
        (): SearchMatches => {
            const subCategoryMatches = new Map<string, number | null>();
            const chartMatches = new Map<string, Set<string>>();
            const subCategoriesNameMatches: string[] = [];
            const chartsNameMatches: string[] = [];
            const subCategoriesNameStarts: string[] = [];
            const chartsNameStarts: string[] = [];

            if (!search) return {
                subCategoryMatches,
                chartMatches,
                subCategoriesNameMatches,
                chartsNameMatches,
                subCategoriesNameStarts,
                chartsNameStarts,
            };

            const includes = (value?: string | null) =>
                !!value && includesInLC(value, search);
            const startsWith = (value?: string | null) =>
                !!value && startsFromInLC(value, search);

            const wordRegex = new RegExp(`\\b${escapeRegExp(search.trim())}\\b`, 'i');

            subCategories.forEach((subCategory) => {
                const subName = subCategory.name ?? '';
                let matchedChartCount = 0;

                (subCategory.charts ?? []).forEach((chart) => {
                    const matchedKeywords = new Set(
                        (chart.keywords ?? []).filter((k): k is string => !!k && wordRegex.test(k)),
                    );

                    if (matchedKeywords.size > 0 || includes(chart.name)) {
                        chartMatches.set(String(chart.id), matchedKeywords);
                        matchedChartCount++;
                    }
                    if (includes(chart.name)) {
                        chartsNameMatches.push(chart.name ?? '');
                    }

                    if (startsWith(chart.name)) {
                        chartsNameStarts.push(chart.name ?? '');
                    }
                });

                if (matchedChartCount > 0 || includes(subName)) {
                    subCategoryMatches.set(subName, matchedChartCount);
                }

                if (includes(subName)) {
                    subCategoriesNameMatches.push(subName);
                }

                if (startsWith(subName)) {
                    subCategoriesNameStarts.push(subName);
                }
            });

            return {
                subCategoryMatches,
                chartMatches,
                subCategoriesNameMatches,
                chartsNameMatches,
                subCategoriesNameStarts,
                chartsNameStarts,
            };
        },
        [search, subCategories]
    );
}
